import { PokemonType } from "types/battle-engine/battle";
import { TYPE_COLORS } from "@/components/cards/card-theme";
import type { TcgCardData } from "@/components/cards/utils/tcg-card-data";

const typeIcon = (type: PokemonType) =>
  `/assets/pokemon-types/${type.toLowerCase()}.svg`;

/** Round energy pip like a TCG energy symbol. */
export const TypePip = ({
  type,
  size = 16,
}: {
  type: PokemonType;
  size?: number;
}) => (
  <div
    className="flex items-center justify-center rounded-full shrink-0"
    style={{
      width: size,
      height: size,
      backgroundColor: TYPE_COLORS[type],
      boxShadow: "inset 0 1px 2px rgba(255,255,255,0.5), 0 1px 2px rgba(0,0,0,0.6)",
    }}
  >
    <img
      src={typeIcon(type)}
      alt={type}
      className="brightness-0 invert"
      style={{ width: size * 0.6, height: size * 0.6 }}
    />
  </div>
);

export const TypePipRow = ({
  types,
  size = 16,
}: {
  types: PokemonType[];
  size?: number;
}) => (
  <div className="flex gap-0.5">
    {types.map((type) => (
      <TypePip key={type} type={type} size={size} />
    ))}
  </div>
);

/** Small "· · ·" rarity indicator (dots scale with rarity). */
export const RarityDots = ({ count }: { count: number }) => (
  <div className="flex gap-[2px]">
    {Array.from({ length: 5 }).map((_, i) => (
      <span
        key={i}
        className="rounded-full w-[4px] h-[4px]"
        style={{ background: i < count ? "#ffd54a" : "rgba(255,255,255,0.25)" }}
      />
    ))}
  </div>
);

/** Weakness / resistance / retreat footer bar shared by several designs. */
export const TcgFooter = ({ d }: { d: TcgCardData }) => (
  <div className="flex justify-between items-center px-1 text-[6px] text-white/80">
    <div className="flex items-center gap-0.5">
      <span className="font-black text-white/50">WK</span>
      {d.weakness ? <TypePip type={d.weakness} size={10} /> : <span>—</span>}
    </div>
    <div className="flex items-center gap-0.5">
      <span className="font-black text-white/50">RS</span>
      {d.resistanceType ? (
        <TypePip type={d.resistanceType} size={10} />
      ) : (
        <span>—</span>
      )}
    </div>
    <div className="flex items-center gap-0.5">
      <span className="font-black text-white/50">RT</span>
      <div className="flex gap-[1px]">
        {Array.from({ length: Math.max(1, d.retreat) }).map((_, i) => (
          <span
            key={i}
            className="rounded-full w-[6px] h-[6px] bg-white/70"
            style={{ opacity: d.retreat === 0 ? 0.25 : 1 }}
          />
        ))}
      </div>
    </div>
  </div>
);

export { typeIcon };

const talentIcon = (type: PokemonType) =>
  `/assets/talents/${type.toLowerCase()}.png`;

/** Compact talent rank chip, e.g. "S". Uses derived purity rank + glow color. */
const RANK_LETTER_COLOR: Record<TcgCardData["talentRank"], string> = {
  "S+": "#fbbf24",
  S: "#f59e0b",
  A: "#c084fc",
  B: "#60a5fa",
  C: "#a3a3a3",
  D: "#f87171",
};

export const TalentBadge = ({
  d,
  showLabel = true,
}: {
  d: TcgCardData;
  showLabel?: boolean;
}) => (
  <div className="flex items-center gap-1 px-1.5 py-0.5 bg-white/15 border border-white/35 rounded-full backdrop-blur-[2px]">
    {showLabel && (
      <span className="font-black text-[7px] text-white/60 uppercase tracking-wider">
        Talent
      </span>
    )}
    <span
      className="font-black text-[14px] leading-none"
      style={{ color: RANK_LETTER_COLOR[d.talentRank] }}
    >
      {d.talentRank}
    </span>
  </div>
);

/** Rank chip + purity meter (x/9) for a richer talent readout. */
export const TalentBar = ({ d }: { d: TcgCardData }) => (
  <div className="flex items-center gap-1">
    <TalentBadge d={d} showLabel={false} />
    <div className="relative flex-1 bg-black/50 border border-white/10 rounded-full h-[4px] overflow-hidden">
      <div
        className="absolute inset-y-0 left-0 rounded-full"
        style={{ width: `${(d.purity / 9) * 100}%`, background: d.talentColor }}
      />
    </div>
    <span className="font-bold text-[6px] text-white/70">{d.purity}/9</span>
  </div>
);

/** 3×3 grid of the rolled talent-type icons (pads empty slots). */
export const TalentIcons = ({ d, size = 12 }: { d: TcgCardData; size?: number }) => (
  <div className="gap-[1px] grid grid-cols-3">
    {Array.from({ length: 9 }).map((_, i) => {
      const type = d.talents[i];
      return (
        <div
          key={i}
          className="flex justify-center items-center bg-black/40 rounded-[2px]"
          style={{ width: size, height: size }}
        >
          {type && (
            <img src={talentIcon(type)} alt={type} style={{ width: size - 2, height: size - 2 }} />
          )}
        </div>
      );
    })}
  </div>
);

export { talentIcon };

/** Retro-RPG segmented stat bars: discrete cells + a large value readout. */
export const SegmentedStatBars = ({
  stats,
  segments = 10,
  max = 180,
}: {
  stats: { ap: number; res: number; spd: number };
  segments?: number;
  max?: number;
}) => {
  const rows: [string, number, string][] = [
    ["AP", stats.ap, "#f8d030"],
    ["RES", stats.res, "#6890f0"],
    ["SPD", stats.spd, "#78c850"],
  ];
  return (
    <div className="flex flex-col gap-[3px]">
      {rows.map(([label, value, color]) => {
        const filled = Math.round((Math.min(max, value) / max) * segments);
        return (
          <div key={label} className="flex items-center gap-1">
            <span className="w-[18px] font-black text-[7px] text-white/60">{label}</span>
            <div className="flex flex-1 gap-[2px]">
              {Array.from({ length: segments }).map((_, i) => (
                <span
                  key={i}
                  className="flex-1 h-[6px] rounded-[1px]"
                  style={{
                    background: i < filled ? color : "rgba(255,255,255,0.12)",
                    boxShadow: i < filled ? `0 0 3px ${color}aa` : "none",
                  }}
                />
              ))}
            </div>
            <span className="w-[20px] font-black text-[11px] text-white text-right leading-none tabular-nums">
              {value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/** One TCG attack row: energy cost pips + name + kind tag + power. */
export const AttackRow = ({
  move,
  compact = false,
}: {
  move: import("@/components/cards/utils/tcg-card-data").TcgMove;
  compact?: boolean;
}) => (
  <div className="flex items-center gap-1 bg-white/8 px-1 py-[2px] border border-white/10 rounded">
    <TypePip type={move.type} size={compact ? 11 : 13} />
    <div className="flex flex-col flex-1 min-w-0">
      <span className="font-bold text-[7px] text-white/90 uppercase leading-tight truncate">
        {move.name}
      </span>
      {!compact && move.kind && (
        <span className="text-[5px] text-white/45 uppercase leading-tight tracking-wide">
          {move.kind}
        </span>
      )}
    </div>
    <span className="font-black text-[10px] text-white leading-none">
      {move.power > 0 ? move.power : "—"}
    </span>
  </div>
);

/** Compact horizontal AP/RES/SPD meters. */
export const MiniStatBars = ({
  stats,
}: {
  stats: { ap: number; res: number; spd: number };
}) => {
  const rows: [string, number, string][] = [
    ["AP", stats.ap, "#f8d030"],
    ["RES", stats.res, "#6890f0"],
    ["SPD", stats.spd, "#78c850"],
  ];
  return (
    <div className="flex flex-col gap-[2px]">
      {rows.map(([label, value, color]) => (
        <div key={label} className="flex items-center gap-1">
          <span className="w-[16px] font-black text-[6px] text-white/50">{label}</span>
          <div className="relative flex-1 bg-black/50 border border-white/10 rounded-full h-[4px] overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${Math.min(100, (value / 180) * 100)}%`, background: color }}
            />
          </div>
          <span className="w-[16px] font-bold text-[6px] text-white/80 text-right">{value}</span>
        </div>
      ))}
    </div>
  );
};
